import type { FlowDocument } from '@segmently/schema-models/v2';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';

export interface PublishArticleParams {
  projectId: string;
  alias: string;
  flowDocument: FlowDocument;
  locale?: string;
  title?: string;
}

export interface PublishArticleResult {
  success: boolean;
  projectId: string;
  alias: string;
  /** Direct static HTML asset URL under assets/articles. */
  url: string;
  assetUrl?: string;
  configUrl: string;
  htmlPath: string;
  configPath: string;
  webShellVersion?: string;
  /** Clean gateway URL on the default domain: /a/{alias}?projectId=... */
  gatewayUrl?: string;
  /** Root URL on the project's article-surface custom domain (when active). */
  customDomainUrl?: string;
  /** Non-blocking SEO/authoring findings from the publish (not displayed in Studio yet). */
  seoWarnings?: string[];
}

export class ArticlePublishApiClient {
  constructor(private readonly baseUrl: string = getBuilderApiBaseUrl()) {}

  async publish(params: PublishArticleParams): Promise<PublishArticleResult> {
    const token = await getAuthToken();
    const response = await fetch(`${this.baseUrl}/api/publish/article`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      const errorText = await response.text();
      let message = `Article publish failed: ${response.status} ${response.statusText} - ${errorText}`;
      try {
        const parsed = JSON.parse(errorText) as { message?: string; error?: string };
        if (parsed.message || parsed.error) {
          message = parsed.message || (parsed.error as string);
        }
      } catch {
        // Non-JSON error body — keep the verbose fallback message.
      }
      throw new Error(message);
    }

    return response.json() as Promise<PublishArticleResult>;
  }
}

async function getAuthToken(): Promise<string> {
  const user = CoreFirebaseService.getInstance().auth.currentUser;
  if (!user) {
    throw new Error('You must be signed in to publish an article');
  }
  return user.getIdToken();
}

function getBuilderApiBaseUrl(): string {
  return import.meta.env.VITE_BUILDER_API_URL || 'http://localhost:8081';
}
